// Clojure course content — Part 1 (lessons 1–11)
// Block types: { t:"p"|"h"|"note"|"list", text|items } and { t:"code", code }

export const lessonsPart1 = [
  {
    id: "welcome",
    title: "Welcome & the REPL",
    group: "Getting Started",
    summary: "What Clojure is, and how running code works here.",
    content: [
      { t: "p", text: "Clojure is a modern programming language from the Lisp family. It runs on top of other platforms — here in your browser it runs as JavaScript. Don't worry if you've never programmed before; we'll build everything up slowly." },
      { t: "p", text: "The first thing to know: in Clojure you spend most of your time writing small expressions and immediately seeing what they produce. An expression is just a piece of code that has a value. For example, the number 5 is an expression whose value is 5." },
      { t: "h", text: "Everything below is live" },
      { t: "p", text: "Every code box in this course is editable and runnable. Click Run (or press Ctrl/Cmd + Enter) to evaluate it right here in your browser. Try changing the number and running it again." },
      { t: "code", code: "42" },
      { t: "p", text: "The little => arrow shows you the value the expression produced. This back-and-forth — type something, see its value — is the heart of Clojure. The tool that does it is called a REPL (Read-Eval-Print Loop). There's a full REPL playground in the top menu for experimenting freely." },
      { t: "h", text: "Comments: notes to yourself" },
      { t: "p", text: "Lines that start with a semicolon — like this ;; — are comments. They're notes for humans; Clojure completely ignores them. You'll see them throughout this course, and they're a great way to leave yourself reminders." },
      { t: "code", code: ";; this whole line is a comment\n(+ 1 2)  ;; and you can tuck a note at the end of a line, too" },
      { t: "note", text: "Get into the habit of commenting early: a one-line 'why' above a tricky expression pays off when you come back to it later. And remember — you learn Clojure by running code, not just reading. Tinker with every example." },
    ],
    exercises: [
      {
        prompt: "Make the expression below evaluate to the number 100.",
        starter: "1",
        tests: ["(= answer 100)"],
        solution: "100",
        hint: "Just replace 1 with 100 and run it.",
      },
    ],
  },

  {
    id: "values",
    title: "Values: Numbers, Text & More",
    group: "Getting Started",
    summary: "The basic kinds of data in Clojure.",
    content: [
      { t: "p", text: "Programs work with data. Clojure has a handful of basic value types you'll use constantly." },
      { t: "h", text: "Numbers" },
      { t: "p", text: "Numbers are written just as you'd expect. Clojure has whole numbers (integers) and decimals." },
      { t: "code", code: "3.14" },
      { t: "note", text: "A small heads-up: numbers in this runtime are JavaScript numbers. Clojure's N suffix for big integers (like 1000000N) is understood, but here it's still a JavaScript number, so very large integers lose precision — on classic JVM Clojure, N means an exact, arbitrarily large integer. And dividing by zero doesn't throw an error: you get ##Inf (infinity), or ##NaN when it's zero divided by zero. You won't need either in this course — just so a surprising result doesn't confuse you." },
      { t: "h", text: "Strings" },
      { t: "p", text: "A string is a piece of text, always wrapped in double quotes." },
      { t: "code", code: "\"Hello there\"" },
      { t: "h", text: "Booleans and nil" },
      { t: "p", text: "true and false are the boolean values, used for yes/no answers. nil means 'nothing' or 'no value' — similar to null in other languages." },
      { t: "code", code: "true" },
      { t: "h", text: "Keywords" },
      { t: "p", text: "A keyword starts with a colon, like :name or :age. Keywords are special labels used all over Clojure, especially as keys in data. They always evaluate to themselves." },
      { t: "code", code: ":favorite-color" },
      { t: "note", text: "Notice keywords can contain dashes. In Clojure we use dashes-between-words instead of camelCase." },
    ],
    exercises: [
      {
        prompt: "Write a string containing your name (any non-empty text in double quotes).",
        starter: "\"\"",
        tests: ["(string? answer)", "(> (count answer) 0)"],
        solution: "\"Ada\"",
        hint: "Put some text between the double quotes, e.g. \"Ada\".",
      },
      {
        prompt: "Create the keyword :done.",
        starter: ";; type the keyword below\n",
        tests: ["(= answer :done)"],
        solution: ":done",
        hint: "Keywords start with a colon, like :done.",
      },
    ],
  },

  {
    id: "prefix",
    title: "Calling Functions",
    group: "Getting Started",
    summary: "How Clojure calls functions with prefix notation.",
    content: [
      { t: "p", text: "In most languages you write 1 + 2. Clojure is different: the operation goes FIRST, inside parentheses. This is called prefix notation." },
      { t: "code", code: "(+ 1 2)" },
      { t: "p", text: "Read that as: 'apply the + function to 1 and 2'. The opening parenthesis means 'call a function'. The first thing after it is the function; everything else are the arguments (the inputs)." },
      { t: "p", text: "This is consistent for everything — subtraction, multiplication, and so on." },
      { t: "code", code: "(* 6 7)" },
      { t: "p", text: "Division works the same way — with one thing worth knowing. Here in the browser, numbers behave like JavaScript numbers: divide two whole numbers and you get a whole number when it divides evenly, otherwise a decimal. So (/ 10 2) is 5, but (/ 7 2) is 3.5." },
      { t: "code", code: "(/ 10 2)" },
      { t: "code", code: "(/ 7 2)" },
      { t: "code", code: "(/ 7.0 2)" },
      { t: "note", text: "Read (/ 7 2) as '7 divided by 2'. Since 7 doesn't split evenly you get the decimal 3.5, and writing 7.0 gives the same 3.5. (On classic JVM Clojure, (/ 7 2) would instead print as the exact ratio 7/2 — this course runs on JavaScript numbers, which give a decimal. Want a whole-number answer? Use quot and rem: (quot 7 2) is 3, (rem 7 2) is 1.) And with several arguments, / divides in turn: (/ 12 2 3) is 12 / 2 / 3 = 2." },
      { t: "p", text: "A big advantage: these functions take as many arguments as you like." },
      { t: "code", code: "(+ 1 2 3 4 5)" },
      { t: "h", text: "Nesting calls" },
      { t: "p", text: "The value of one call can be an argument to another. Clojure works from the inside out." },
      { t: "code", code: "(+ 10 (* 2 3))" },
      { t: "note", text: "The expression (* 2 3) becomes 6 first, then (+ 10 6) becomes 16." },
    ],
    exercises: [
      {
        prompt: "Add the numbers 15, 25 and 60 together (the result should be 100).",
        starter: "(+ )",
        tests: ["(= answer 100)"],
        solution: "(+ 15 25 60)",
        hint: "The + function can take three arguments: (+ 15 25 60).",
      },
      {
        prompt: "Compute (4 + 6) multiplied by 5 using nested calls. Result should be 50.",
        starter: "(* )",
        tests: ["(= answer 50)"],
        solution: "(* (+ 4 6) 5)",
        hint: "Put the addition inside the multiplication: (* (+ 4 6) 5).",
      },
    ],
  },

  {
    id: "def",
    title: "Naming Things with def",
    group: "Building Blocks",
    summary: "Give values a name so you can reuse them.",
    content: [
      { t: "p", text: "When a value is useful, you give it a name so you can refer to it later. The def form creates a named value." },
      { t: "code", code: "(def pi 3.14159)\npi" },
      { t: "p", text: "After defining pi, we can just write pi anywhere and Clojure substitutes its value. Names can then be combined in expressions." },
      { t: "code", code: "(def radius 10)\n(def area-of-circle (* pi radius radius))\narea-of-circle" },
      { t: "note", text: "In Clojure these names normally don't change once set — values are stable. That predictability makes programs easier to reason about." },
      { t: "p", text: "Names can hold any value: numbers, strings, keywords, anything." },
      { t: "code", code: "(def greeting \"Good morning\")\ngreeting" },
    ],
    exercises: [
      {
        prompt: "Define a name `age` with the value 30.",
        starter: "(def age )",
        tests: ["(= age 30)"],
        solution: "(def age 30)",
        hint: "The shape is (def name value): (def age 30).",
      },
      {
        prompt: "Define `width` as 8 and `height` as 5, then the exercise checks their product. (Define both.)",
        starter: "(def width )\n(def height )",
        tests: ["(= (* width height) 40)"],
        solution: "(def width 8)\n(def height 5)",
        hint: "Two separate def forms, then 8 * 5 = 40.",
      },
    ],
  },

  {
    id: "defn",
    title: "Writing Your Own Functions",
    group: "Building Blocks",
    summary: "Package logic into reusable functions with defn.",
    content: [
      { t: "p", text: "A function is a reusable recipe: it takes some inputs, does something, and gives back a result. You create one with defn." },
      { t: "code", code: "(defn square [x]\n  (* x x))\n\n(square 5)" },
      { t: "p", text: "Let's break that down: defn starts the definition, square is the name, [x] is the list of parameters (the inputs — here just one, called x), and (* x x) is the body that computes the result." },
      { t: "p", text: "A function automatically returns the value of the last expression in its body. There's no 'return' keyword needed." },
      { t: "h", text: "Multiple parameters" },
      { t: "code", code: "(defn add [a b]\n  (+ a b))\n\n(add 3 4)" },
      { t: "p", text: "You can call your function as many times as you like with different inputs." },
      { t: "code", code: "(defn greet [name]\n  (str \"Hello, \" name \"!\"))\n\n(greet \"Sam\")" },
      { t: "note", text: "str glues values together into one string. We'll cover it more later." },
    ],
    exercises: [
      {
        prompt: "Write a function `double` that takes a number and returns it multiplied by 2.",
        starter: "(defn double [x]\n  )",
        tests: ["(= (double 4) 8)", "(= (double 0) 0)", "(= (double 21) 42)"],
        solution: "(defn double [x]\n  (* x 2))",
        hint: "The body should be (* x 2).",
      },
      {
        prompt: "Write a function `full-name` that takes a first and last name and returns them joined with a space, e.g. (full-name \"Ada\" \"Lovelace\") => \"Ada Lovelace\".",
        starter: "(defn full-name [first last]\n  )",
        tests: ["(= (full-name \"Ada\" \"Lovelace\") \"Ada Lovelace\")", "(= (full-name \"Alan\" \"Turing\") \"Alan Turing\")"],
        solution: "(defn full-name [first last]\n  (str first \" \" last))",
        hint: "Use str with three parts: first, a space \" \", and last.",
      },
    ],
  },

  {
    id: "conditionals",
    title: "Making Decisions: if, when, cond",
    group: "Building Blocks",
    summary: "Run different code depending on a condition.",
    content: [
      { t: "p", text: "Programs need to make choices. The if form picks between two options based on a test." },
      { t: "code", code: "(if (> 5 3)\n  \"five is bigger\"\n  \"three is bigger\")" },
      { t: "p", text: "The shape is (if test then-value else-value). If the test is true, if returns the second part; otherwise it returns the third part. Here (> 5 3) asks 'is 5 greater than 3?'." },
      { t: "code", code: "(defn describe [n]\n  (if (even? n)\n    \"even\"\n    \"odd\"))\n\n(describe 7)" },
      { t: "h", text: "when: an if with no else" },
      { t: "p", text: "When you only care about the true case, use when. If the test is false it just returns nil." },
      { t: "code", code: "(when (> 10 5)\n  \"ten is greater\")" },
      { t: "h", text: "cond: many branches" },
      { t: "p", text: "For several conditions, cond checks each test in order and returns the value for the first true one. :else is a catch-all." },
      { t: "code", code: "(defn grade [score]\n  (cond\n    (>= score 90) \"A\"\n    (>= score 80) \"B\"\n    (>= score 70) \"C\"\n    :else \"F\"))\n\n(grade 85)" },
    ],
    exercises: [
      {
        prompt: "Write `sign` that returns \"positive\" if n > 0, otherwise \"not positive\".",
        starter: "(defn sign [n]\n  )",
        tests: ["(= (sign 5) \"positive\")", "(= (sign -3) \"not positive\")", "(= (sign 0) \"not positive\")"],
        solution: "(defn sign [n]\n  (if (> n 0) \"positive\" \"not positive\"))",
        hint: "Use (if (> n 0) ...).",
      },
      {
        prompt: "Write `water-state` using cond: below 0 => \"ice\", 0 to 99 => \"liquid\", 100 or above => \"steam\".",
        starter: "(defn water-state [temp]\n  (cond\n    ))",
        tests: ["(= (water-state -5) \"ice\")", "(= (water-state 20) \"liquid\")", "(= (water-state 100) \"steam\")", "(= (water-state 0) \"liquid\")"],
        solution: "(defn water-state [temp]\n  (cond\n    (< temp 0) \"ice\"\n    (< temp 100) \"liquid\"\n    :else \"steam\"))",
        hint: "Check (< temp 0) first, then (< temp 100), then :else.",
      },
    ],
  },

  {
    id: "logic",
    title: "Truth & Logic",
    group: "Building Blocks",
    summary: "Truthiness and combining conditions with and/or/not.",
    content: [
      { t: "p", text: "Clojure has a simple rule for what counts as 'true'. Only two things are considered false: the value false and nil. EVERYTHING else — including 0 and empty strings — is treated as true (we say 'truthy')." },
      { t: "code", code: "(if 0 \"zero is truthy\" \"zero is falsey\")" },
      { t: "h", text: "Comparisons" },
      { t: "p", text: "Comparison functions return true or false: = (equal), not= , <, >, <=, >=." },
      { t: "code", code: "(= 3 3)" },
      { t: "h", text: "Combining with and / or / not" },
      { t: "p", text: "`and` is true only if all its arguments are truthy.\n`or` is true if any is truthy.\n`not` flips a value." },
      { t: "code", code: "(and (> 5 1) (< 5 10))" },
      { t: "code", code: "(or false nil \"found one\")" },
      { t: "note", text: "Handy detail: and returns the last truthy value, and or returns the first truthy value — not just true/false. This is often used for defaults." },
    ],
    exercises: [
      {
        prompt: "Write `teenager?` that returns true when age is between 13 and 19 (inclusive).",
        starter: "(defn teenager? [age]\n  )",
        tests: ["(= (teenager? 15) true)", "(= (teenager? 13) true)", "(= (teenager? 19) true)", "(= (teenager? 20) false)", "(= (teenager? 10) false)"],
        solution: "(defn teenager? [age]\n  (and (>= age 13) (<= age 19)))",
        hint: "Combine two comparisons with and.",
      },
      {
        prompt: "Write `weekend?` that returns true if the day is \"Sat\" or \"Sun\".",
        starter: "(defn weekend? [day]\n  )",
        tests: ["(= (weekend? \"Sat\") true)", "(= (weekend? \"Sun\") true)", "(= (weekend? \"Mon\") false)"],
        solution: "(defn weekend? [day]\n  (or (= day \"Sat\") (= day \"Sun\")))",
        hint: "Use or with two equality checks: (or (= day \"Sat\") (= day \"Sun\")). Each check already returns true/false, so or gives back a boolean.",
      },
    ],
  },

  {
    id: "vectors",
    title: "Vectors: Ordered Collections",
    group: "Collections",
    summary: "The everyday list-like collection in Clojure.",
    content: [
      { t: "p", text: "A vector is an ordered collection of values, written with square brackets. It's the collection you'll reach for most often." },
      { t: "code", code: "[1 2 3 4 5]" },
      { t: "p", text: "The function `vector` can also be used to create vectors." },
      { t: "code", code: "(vector 1 2 3 4 5)" },
      { t: "p", text: "Vectors can hold any values, even mixed types." },
      { t: "code", code: "[\"apple\" 42 :fruit true]" },
      { t: "h", text: "Reading elements" },
      { t: "p", text: "Positions start at 0. Use nth to get an element by position, or the handy first and last." },
      { t: "code", code: "(def colors [\"red\" \"green\" \"blue\"])\n(nth colors 1)" },
      { t: "h", text: "Other ways to get an element by position" },
      { t: "p", text: "Using `get`" },
      { t: "code", code: "(get colors 1)" },
      { t: "p", text: "Using the vector itself as a function!" },
      { t: "code", code: "(colors 1)" },
      { t: "h", text: "Use `first` to get the first element of a vector" },
      { t: "code", code: "(first [10 20 30])" },
      { t: "h", text: "count and conj" },
      { t: "p", text: "count tells you how many items there are. conj adds an item — for vectors it adds to the END." },
      { t: "code", code: "(count [1 2 3])" },
      { t: "code", code: "(conj [1 2 3] 4)" },
      { t: "note", text: "conj doesn't change the original vector — it returns a NEW one with the item added. Clojure's data is immutable." },
    ],
    exercises: [
      {
        prompt: "Create a vector containing the numbers 10, 20, and 30 (in that order).",
        starter: ";; write the vector\n",
        tests: ["(= answer [10 20 30])"],
        solution: "[10 20 30]",
        hint: "Use square brackets: [10 20 30].",
      },
      {
        prompt: "Write `third` that returns the third element of a vector (position index 2).",
        starter: "(defn third [v]\n  )",
        tests: ["(= (third [1 2 3 4]) 3)", "(= (third [:a :b :c]) :c)"],
        solution: "(defn third [v]\n  (nth v 2))",
        hint: "Positions start at 0, so the third element is index 2: (nth v 2).",
      },
      {
        prompt: "Write `add-item` that adds x to the end of vector v and returns the new vector.",
        starter: "(defn add-item [v x]\n  )",
        tests: ["(= (add-item [1 2] 3) [1 2 3])", "(= (add-item [] 1) [1])"],
        solution: "(defn add-item [v x]\n  (conj v x))",
        hint: "conj adds to the end of a vector.",
      },
    ],
  },

  {
    id: "maps",
    title: "Maps: Key–Value Data",
    group: "Collections",
    summary: "Store labelled data with maps.",
    content: [
      { t: "p", text: "A map associates keys with values, like a dictionary. It's written with curly braces. Keywords make great keys." },
      { t: "code", code: "{:name \"Ada\" :age 36 :city \"London\"}" },
      { t: "h", text: "Looking up values" },
      { t: "p", text: "Use get, or simply put the keyword first as if it were a function — keywords can look themselves up in a map." },
      { t: "code", code: "(def person {:name \"Ada\" :age 36})\n(get person :name)" },
      { t: "code", code: "(:age person)" },
      { t: "h", text: "Adding and changing" },
      { t: "p", text: "assoc returns a new map with a key added or updated. dissoc returns a new map with a key removed." },
      { t: "code", code: "(assoc person :city \"London\")" },
      { t: "code", code: "(dissoc person :age)" },
      { t: "h", text: "update" },
      { t: "p", text: "update applies a function to the value at a key. Here we add 1 to the age." },
      { t: "code", code: "(update person :age inc)" },
      { t: "note", text: "keys and vals give you all the keys or all the values of a map." },
    ],
    exercises: [
      {
        prompt: "Create a map for a book with :title \"Clojure\" and :pages 300.",
        starter: ";; write the map\n",
        tests: ["(= answer {:title \"Clojure\" :pages 300})"],
        solution: "{:title \"Clojure\" :pages 300}",
        hint: "Curly braces with keyword keys: {:title \"Clojure\" :pages 300}.",
      },
      {
        prompt: "Write `get-price` that returns the :price from a product map.",
        starter: "(defn get-price [product]\n  )",
        tests: ["(= (get-price {:price 9.99 :name \"Pen\"}) 9.99)", "(= (get-price {:price 100}) 100)"],
        solution: "(defn get-price [product]\n  (:price product))",
        hint: "Use the keyword as a function: (:price product).",
      },
      {
        prompt: "Write `set-status` that adds/updates the :status key to \"active\" in the given map.",
        starter: "(defn set-status [m]\n  )",
        tests: ["(= (set-status {:id 1}) {:id 1 :status \"active\"})", "(= (:status (set-status {})) \"active\")"],
        solution: "(defn set-status [m]\n  (assoc m :status \"active\"))",
        hint: "Use assoc to add the :status key.",
      },
    ],
  },

  {
    id: "sets-lists",
    title: "Sets & Lists",
    group: "Collections",
    summary: "Two more collections and when to use them.",
    content: [
      { t: "p", text: "Besides vectors and maps, Clojure has sets and lists." },
      { t: "h", text: "Sets: unique values" },
      { t: "p", text: "A set holds unique values with no duplicates, written with #{}. Great for membership questions." },
      { t: "code", code: "#{:red :green :blue}" },
      { t: "p", text: "A set can be used as a function to test membership: it returns the value if present, otherwise nil." },
      { t: "code", code: "(def primary #{:red :green :blue})\n(primary :green)" },
      { t: "code", code: "(contains? #{1 2 3} 2)" },
      { t: "h", text: "Lists" },
      { t: "p", text: "Lists are written with parentheses and a quote mark. Because plain parentheses mean 'call a function', we put a ' in front to say 'this is data, not a call'." },
      { t: "code", code: "'(1 2 3)" },
      { t: "p", text: "In everyday Clojure you'll use vectors far more than lists. Lists matter most for the code itself (remember, code is written in parentheses!). For now, prefer vectors for your data." },
      { t: "note", text: "conj adds to the FRONT of a list, but to the END of a vector — a subtle but useful difference." },
    ],
    exercises: [
      {
        prompt: "Write `has-color?` that returns true if `color` is in the set #{:red :green :blue}.",
        starter: "(def palette #{:red :green :blue})\n\n(defn has-color? [color]\n  )",
        tests: ["(= (has-color? :red) true)", "(= (has-color? :yellow) false)"],
        solution: "(def palette #{:red :green :blue})\n\n(defn has-color? [color]\n  (contains? palette color))",
        hint: "Use (contains? palette color).",
      },
      {
        prompt: "Write `unique` that turns a vector into a set of its distinct values.",
        starter: "(defn unique [v]\n  )",
        tests: ["(= (unique [1 1 2 3 3]) #{1 2 3})", "(= (unique [:a :a]) #{:a})"],
        solution: "(defn unique [v]\n  (set v))",
        hint: "The set function converts a collection into a set.",
      },
    ],
  },

  {
    id: "sequences",
    title: "The Sequence Idea",
    group: "Collections",
    summary: "One toolbox of functions that works on every collection.",
    content: [
      { t: "p", text: "Here's one of Clojure's best ideas: nearly all collections can be viewed as a sequence — an ordered walk through their elements. That means the same handful of functions works on vectors, lists, sets, and even maps." },
      { t: "code", code: "(first [10 20 30])" },
      { t: "code", code: "(rest [10 20 30])" },
      { t: "p", text: "first gives the head; rest gives everything after the head. count works everywhere too." },
      { t: "code", code: "(count {:a 1 :b 2})" },
      { t: "h", text: "Building sequences" },
      { t: "p", text: "range makes a sequence of numbers. It's great for generating data to work with." },
      { t: "code", code: "(range 5)" },
      { t: "code", code: "(range 2 8)" },
      { t: "h", text: "into" },
      { t: "p", text: "into pours all elements of one collection into another. It's a common way to convert or combine collections." },
      { t: "code", code: "(into [] (range 3))" },
      { t: "note", text: "Because these functions work on every collection, once you learn them you can process almost any data the same way. That's the payoff of the sequence idea — and it sets up the next few lessons." },
    ],
    exercises: [
      {
        prompt: "Write `count-items` that returns how many elements are in any collection.",
        starter: "(defn count-items [coll]\n  )",
        tests: ["(= (count-items [1 2 3]) 3)", "(= (count-items #{:a :b}) 2)", "(= (count-items \"hello\") 5)"],
        solution: "(defn count-items [coll]\n  (count coll))",
        hint: "count works on all of them.",
      },
      {
        prompt: "Use range to build a vector of the numbers 0 through 9. Name it `nums`.",
        starter: "(def nums )",
        tests: ["(= nums [0 1 2 3 4 5 6 7 8 9])"],
        solution: "(def nums (into [] (range 10)))",
        hint: "(range 10) gives 0..9; use (into [] ...) to make it a vector.",
      },
    ],
  },
];
